// modules/website/pages/DynamicPage.tsx - Página Dinâmica Renderizável

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TenantPage } from '../../../core/types';
import { ComponentRenderer } from '../components/ComponentRenderer';
import { useTenant } from '../hooks/useTenant';
import { tenantCollection } from '../../../core/services/firebase/firestore';
import { getDocs } from 'firebase/firestore';

export const DynamicPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { tenant, loading: tenantLoading } = useTenant();
  const [page, setPage] = useState<TenantPage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenant) {
      loadPage();
    }
  }, [tenant, slug]);

  const loadPage = async () => {
    try {
      setLoading(true);

      let foundPage: TenantPage | undefined;

      // 1. Verificar se a página existe no objeto do tenant (Demo/Cache)
      if (tenant?.pages && Array.isArray(tenant.pages)) {
        foundPage = tenant.pages.find(p => {
          if (!slug) return p.isHomepage;
          return p.slug === slug; // && p.isPublished check usually not needed for demo but can add
        }) as TenantPage | undefined;
      }

      // 2. Se não encontrou e não tem pages no tenant, buscar no Firestore
      if (!foundPage && (!tenant?.pages || tenant.pages.length === 0)) {
        // Buscar página no Firestore
        const pagesRef = tenantCollection('pages');
        const snapshot = await getDocs(pagesRef);
        
        foundPage = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as TenantPage))
          .find(p => {
            if (!slug) return p.isHomepage;
            return p.slug === slug && p.isPublished;
          });
      }

      setPage(foundPage || null);
    } catch (error) {
      console.error('Erro ao carregar página:', error);
    } finally {
      setLoading(false);
    }
  };

  if (tenantLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-gray-300 mb-4">404</h1>
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">Página não encontrada</h2>
          <p className="text-gray-500">A página que você procura não existe ou foi removida.</p>
          <a 
            href="/"
            className="mt-6 inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Voltar para o Início
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Renderizar componentes da página */}
      {page.components
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((component) => (
          <ComponentRenderer key={component.id} component={component} />
        ))}
    </div>
  );
};
